/**
 * ExecutionOverlay — live visualization of an agent run's SSE event stream.
 *
 * Given an `agentId`, it POSTs to `/api/agents/{id}/run` and consumes the
 * `text/event-stream` (via {@link runAgentStream}). As `step.started` /
 * `step.completed` / `step.failed` events arrive it:
 *   - updates a per-node run status in the canvas store so each node lights up
 *     in place (running → completed/failed), and
 *   - appends a row to a live step log shown in a side panel, ending with the
 *     terminal `run.completed` / `run.failed` summary + final output payload.
 *
 * The overlay manages its own stream lifecycle: it starts on mount, exposes a
 * Cancel button that aborts the fetch, and clears node statuses on close.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  RUN_EVENT_TYPES,
  isTerminalRunEvent,
  type RunEvent,
} from '../../lib/agents';
import { runAgentStream, type RunStreamHandle } from '../../lib/runStream';
import { useCanvasStore } from '../../stores/canvasStore';
import type { BaseNodeData, NodeRunStatus } from './BaseNode';

interface ExecutionOverlayProps {
  agentId: string;
  /** Run inputs forwarded to the backend (merged over agent defaults). */
  inputPayload?: Record<string, unknown>;
  /** Close the overlay (clears node statuses). */
  onClose: () => void;
}

interface StepLogEntry {
  key: string;
  nodeId: string;
  nodeName: string;
  status: NodeRunStatus;
  error?: string;
}

type RunPhase = 'running' | 'completed' | 'failed' | 'cancelled';

export function ExecutionOverlay({
  agentId,
  inputPayload,
  onClose,
}: ExecutionOverlayProps): ReactNode {
  const [log, setLog] = useState<StepLogEntry[]>([]);
  const [phase, setPhase] = useState<RunPhase>('running');
  const [summary, setSummary] = useState<string | null>(null);
  const [output, setOutput] = useState<Record<string, unknown> | null>(null);
  const handleRef = useRef<RunStreamHandle | null>(null);

  // Read store setters imperatively so the effect runs exactly once per agent.
  const setNodeStatus = (nodeId: string, status: NodeRunStatus): void => {
    useCanvasStore.setState((state) => ({
      nodes: state.nodes.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, runStatus: status } }
          : n,
      ),
    }));
  };

  const clearNodeStatuses = (): void => {
    useCanvasStore.setState((state) => ({
      nodes: state.nodes.map((n) => {
        const data = n.data as BaseNodeData;
        if (data.runStatus === undefined) return n;
        const { runStatus: _drop, ...rest } = data;
        return { ...n, data: rest };
      }),
    }));
  };

  useEffect(() => {
    setLog([]);
    setPhase('running');
    setSummary(null);
    setOutput(null);

    const handleEvent = (event: RunEvent): void => {
      switch (event.event_type) {
        case RUN_EVENT_TYPES.STEP_STARTED: {
          if (event.node_id) setNodeStatus(event.node_id, 'running');
          setLog((prev) => [
            ...prev,
            {
              key: event.step_id ?? `${event.node_id}-start`,
              nodeId: event.node_id ?? '',
              nodeName: event.node_name ?? event.node_id ?? 'step',
              status: 'running',
            },
          ]);
          break;
        }
        case RUN_EVENT_TYPES.STEP_COMPLETED: {
          if (event.node_id) setNodeStatus(event.node_id, 'completed');
          setLog((prev) =>
            updateStep(prev, event.node_id, { status: 'completed' }),
          );
          break;
        }
        case RUN_EVENT_TYPES.STEP_FAILED: {
          if (event.node_id) setNodeStatus(event.node_id, 'failed');
          setLog((prev) =>
            updateStep(prev, event.node_id, {
              status: 'failed',
              error: event.error,
            }),
          );
          break;
        }
        case RUN_EVENT_TYPES.RUN_COMPLETED: {
          setPhase('completed');
          setSummary(
            `Completed ${event.steps_completed ?? 0} step(s)` +
              (event.duration_ms != null
                ? ` in ${Math.round(event.duration_ms)}ms`
                : ''),
          );
          setOutput(event.output_payload ?? null);
          break;
        }
        case RUN_EVENT_TYPES.RUN_FAILED: {
          setPhase('failed');
          setSummary(event.error ?? 'Run failed');
          break;
        }
        case RUN_EVENT_TYPES.RUN_CANCELLED: {
          setPhase('cancelled');
          setSummary('Run cancelled');
          break;
        }
        default: {
          if (isTerminalRunEvent(event.event_type)) setPhase('completed');
          break;
        }
      }
    };

    const handle = runAgentStream(
      agentId,
      { input_payload: inputPayload ?? {} },
      {
        onEvent: handleEvent,
        onError: (error) => {
          setPhase('failed');
          setSummary(error.message);
        },
      },
    );
    handleRef.current = handle;

    return () => {
      handle.cancel();
      clearNodeStatuses();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const cancel = (): void => {
    handleRef.current?.cancel();
    setPhase('cancelled');
    setSummary('Run cancelled');
  };

  return (
    <div
      className="absolute top-0 right-0 h-full w-80 bg-white border-l border-steel-200 shadow-xl flex flex-col z-10"
      data-testid="execution-overlay"
      data-run-phase={phase}
    >
      <div className="px-4 py-3 border-b border-steel-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-navy-900">Run</h3>
          <p className="text-[11px] text-steel-500">Live execution trace</p>
        </div>
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${PHASE_BADGE[phase]}`}
          data-testid="run-phase-badge"
        >
          {phase}
        </span>
      </div>

      <ol className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="step-log">
        {log.map((entry) => (
          <li
            key={entry.key}
            data-testid="step-log-entry"
            data-node-id={entry.nodeId}
            data-status={entry.status}
            className="rounded-md border border-steel-200 px-3 py-2"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-navy-900 truncate">
                {entry.nodeName}
              </span>
              <StatusDot status={entry.status} />
            </div>
            {entry.error && (
              <p className="mt-1 text-[11px] text-rose-600">{entry.error}</p>
            )}
          </li>
        ))}
        {log.length === 0 && (
          <li className="text-[11px] text-steel-500">Waiting for events…</li>
        )}
      </ol>

      {summary && (
        <div className="px-4 py-2 border-t border-steel-200 text-[12px] text-navy-700">
          {summary}
        </div>
      )}

      {output && (
        <pre
          data-testid="run-output"
          className="px-4 py-2 border-t border-steel-200 text-[11px] text-steel-700 max-h-40 overflow-auto whitespace-pre-wrap"
        >
          {JSON.stringify(output, null, 2)}
        </pre>
      )}

      <div className="px-4 py-3 border-t border-steel-200 flex gap-2">
        {phase === 'running' ? (
          <button type="button" className="btn-secondary flex-1" onClick={cancel}>
            Cancel
          </button>
        ) : (
          <button type="button" className="btn-primary flex-1" onClick={onClose}>
            Close
          </button>
        )}
      </div>
    </div>
  );
}

const PHASE_BADGE: Record<RunPhase, string> = {
  running: 'bg-sky-100 text-sky-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-steel-100 text-steel-600',
};

function StatusDot({ status }: { status: NodeRunStatus }): ReactNode {
  const cls: Record<NodeRunStatus, string> = {
    idle: 'bg-steel-300',
    running: 'bg-sky-400 animate-pulse',
    completed: 'bg-emerald-500',
    failed: 'bg-rose-500',
  };
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${cls[status]}`}
      aria-label={status}
    />
  );
}

/** Update the most-recent log entry for `nodeId` with a patch. */
function updateStep(
  log: StepLogEntry[],
  nodeId: string | undefined,
  patch: Partial<StepLogEntry>,
): StepLogEntry[] {
  if (!nodeId) return log;
  // Find the last entry for this node (the in-flight one).
  for (let i = log.length - 1; i >= 0; i -= 1) {
    const entry = log[i];
    if (entry && entry.nodeId === nodeId) {
      const next = log.slice();
      next[i] = { ...entry, ...patch };
      return next;
    }
  }
  return log;
}

export default ExecutionOverlay;
